import React, { useState, useEffect } from "react";
import {
  Modal,
  TextInput,
  CheckboxGroup,
  Checkbox,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
  ProgressIndicatorSkeleton,
  Loading,
} from "@carbon/react";
import "./clientBindingModal.scss";
import { useTranslation } from "react-i18next";
import { useAddChat } from "../../hooks/useAddChat";
import { useGetChatMembers } from "../../hooks/useGetChatMembers";
import { useAddMemberToChat } from "../../hooks/useAddMemberToChat";
import { queryClient } from "../..";
import ReactGA from "react-ga4";
import { useAddCoworker } from "../../hooks/useAddCoworker";

export default function ClientBindingModal({
  open,
  setOpen,
  guid,
  allChatNames = [],
  clientCoworkers,
  isClientInfoPending,
  userPublicName,
  setToastType,
}) {
  const [step, setStep] = useState(null);
  const [isCoworkers, setIsCoworkers] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [validation, setValidation] = useState({
    invalid: false,
    invalidText: "",
  });
  const [coworkerValue, setCoworkerValue] = useState("");
  const [allMembers, setAllMembers] = useState([]);
  const [originalPossible, setOriginalPossible] = useState([]);
  const [createdChatId, setCreatedChatId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [notification, setNotification] = useState({
    visibility: false,
    title: "",
    subtitle: "",
    kind: "",
  });

  const { t } = useTranslation();
  const STEPS = {
    COWORKER: 0,
    CHAT: 1,
    MEMBERS: 2,
  };

  const mutationAddChat = useAddChat();
  const mutationAllMembers = useGetChatMembers();
  const mutationAddMember = useAddMemberToChat();
  const mutationAddCoworker = useAddCoworker();

  useEffect(() => {
    const hasCoworkers = clientCoworkers?.length > 0;

    setStep(hasCoworkers ? STEPS.CHAT : STEPS.COWORKER);
    setIsCoworkers(hasCoworkers);
  }, [clientCoworkers]);

  useEffect(() => {
    ReactGA.event("screen_view", {
      firebase_screen: "ClientBindingModal",
      firebase_screen_class: "ChatInfoPage",
    });
  }, []);

  useEffect(() => {
    const trimmedValue = inputValue.trim();

    if (allChatNames.includes(trimmedValue)) {
      setValidation({
        invalid: true,
        invalidText: `${t("main.chat.addModal.validationText.existedValue")}`,
      });
    } else {
      setValidation({ invalid: false, invalidText: "" });
    }
  }, [inputValue, setValidation, allChatNames, t]);

  const handleCheckboxChange = (chataccid) => {
    setAllMembers((prev) =>
      prev.map((m) =>
        m.chataccid === chataccid ? { ...m, checked: !m.checked } : m,
      ),
    );
  };

  const handleInputChange = (setter) => (e) => {
    setter(e.target.value);
    if (validation.invalid) {
      setValidation({ invalid: false, invalidText: "" });
    }
  };

  const validateRequired = (value) => {
    if (!value.trim()) {
      setValidation({
        invalid: true,
        invalidText: t("main.chat.addModal.validationText.noValue"),
      });
      return false;
    }
    return true;
  };

  const handlePrimary = async () => {
    if (step === STEPS.COWORKER && !isCoworkers) {
      if (!validateRequired(coworkerValue)) return;

      mutationAddCoworker.mutate(
        { guid, nick: coworkerValue.trim() },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: ["chat_client_info"],
            });
            setStep(STEPS.CHAT);
          },
          onError: () => {
            setNotification({
              visibility: true,
              title: "authentication.notification.requestError500.title",
              subtitle:
                "authentication.notification.requestError500.serverError",
              kind: "error",
            });
          },
        },
      );

      return;
    }
    if (step === STEPS.CHAT) {
      //   setLoading("active");
      if (validation.invalid) return;

      if (!validateRequired(inputValue)) return;
      try {
        const addChatResponse = await mutationAddChat.mutateAsync({
          guid,
          name: inputValue.trim(),
        });

        const chatId = addChatResponse?.data?.data?.chatid;
        if (!chatId) throw new Error("No chatId received");
        setCreatedChatId(chatId);

        const membersResponse = await mutationAllMembers.mutateAsync({
          guid,
          chatId: chatId,
        });

        const membersWithChecked = membersResponse?.data?.data.members.map(
          (m) => ({
            ...m,
            checked: true,
          }),
        );

        const possibleFiltered = membersResponse?.data?.data.possible.map(
          (p) => ({
            ...p,
            checked: false,
          }),
        );

        const merged = [...membersWithChecked, ...possibleFiltered];

        setAllMembers(merged);
        setOriginalPossible(membersResponse?.data?.data.possible);

        queryClient.invalidateQueries({ queryKey: ["chat_list"] });

        setStep(STEPS.MEMBERS);
      } catch (error) {
        console.log(error);
        setNotification({
          visibility: true,
          title: "authentication.notification.requestError500.title",
          subtitle: "authentication.notification.requestError500.serverError",
          kind: "error",
        });
      } finally {
      }
    }
    if (step === STEPS.MEMBERS) {
      setSubmitting(true);

      const currentMembers = allMembers.filter((m) => m.checked);
      const addedSelf = currentMembers.some((m) =>
        m.name.includes(userPublicName),
      );
      const oldPossibleIds = new Set(originalPossible.map((m) => m.chataccid));
      const toAdd = currentMembers.filter((m) =>
        oldPossibleIds.has(m.chataccid),
      );

      if (toAdd.length === 0) {
        setToastType("warning");
        setOpen(false);
        resetModalState();
        setSubmitting(false);
        return;
      }

      try {
        const addPromises = toAdd.map((item) =>
          mutationAddMember.mutateAsync({
            guid,
            chatId: createdChatId,
            chataccid: item.chataccid,
          }),
        );

        const results = await Promise.allSettled(addPromises);
        const anyError = results.some((r) => r.status === "rejected");

        if (anyError) {
          setNotification({
            visibility: true,
            title: "authentication.notification.requestError500.title",
            subtitle: "authentication.notification.requestError500.serverError",
            kind: "error",
          });
          return;
        }

        if (addedSelf) setToastType("success");
        else setToastType("warning");
        setOpen(false);
        resetModalState();
      } catch (err) {
        console.error(err);
        setNotification({
          visibility: true,
          title: "authentication.notification.requestError500.title",
          subtitle: "authentication.notification.requestError500.serverError",
          kind: "error",
        });
      } finally {
        setSubmitting(false);
      }
    }
  };

  const resetModalState = () => {
    setStep(0);
    setInputValue("");
    setCoworkerValue("");
    setValidation({ invalid: false, invalidText: "" });
    setAllMembers([]);
    setOriginalPossible([]);
    setCreatedChatId(null);
  };

  const handleCloseModal = () => {
    setToastType("warning");
    setOpen(false);
    resetModalState();
  };

  return (
    <Modal
      open={!!open}
      onRequestClose={handleCloseModal}
      modalHeading={`${t("main.chat.clientBindingModal.title")}`}
      modalLabel={`${t("main.chat.clientBindingModal.label")}`}
      primaryButtonText={
        step !== STEPS.MEMBERS
          ? `${t("main.chat.clientBindingModal.next")}`
          : `${t("main.chat.clientBindingModal.save")}`
      }
      primaryButtonDisabled={submitting}
      secondaryButtonText={`${t("main.chat.addModal.cancel")}`}
      onRequestSubmit={handlePrimary}
      onSecondarySubmit={handleCloseModal}
      closeButtonLabel={`${t("main.modalText.close")}`}
      className="client_binding"
    >
      <div className="client_binding_progress">
        {isClientInfoPending || step === null ? (
          <ProgressIndicatorSkeleton />
        ) : (
          <ProgressIndicator currentIndex={step}>
            {!isCoworkers && (
              <ProgressStep
                label={t("main.chat.clientBindingModal.stepCoworkersLabel")}
                description={t(
                  "main.chat.clientBindingModal.stepCoworkersDesc",
                  {
                    step: 1,
                  },
                )}
                secondaryLabel={t(
                  "main.chat.clientBindingModal.progressRequired",
                )}
              />
            )}
            <ProgressStep
              label={t("main.chat.clientBindingModal.step1label")}
              description={t("main.chat.clientBindingModal.step1Desc", {
                step: isCoworkers ? 1 : 2,
              })}
              secondaryLabel={t(
                "main.chat.clientBindingModal.progressRequired",
              )}
            />

            <ProgressStep
              label={t("main.chat.clientBindingModal.step2label")}
              description={t("main.chat.clientBindingModal.step2Desc", {
                step: isCoworkers ? 2 : 3,
              })}
              secondaryLabel={t(
                "main.chat.clientBindingModal.progressRequired",
              )}
            />
          </ProgressIndicator>
        )}
      </div>

      {isClientInfoPending ? (
        <Loading small />
      ) : (
        step === STEPS.COWORKER &&
        !isCoworkers && (
          <div className="client_binding_main">
            <TextInput
              id="coworker-input"
              labelText={t("main.chat.addModal.title", {
                context: "coworkers",
              })}
              placeholder={`${t("main.chat.addModal.placeholder", {
                context: "coworkers",
              })}`}
              value={coworkerValue}
              onChange={handleInputChange(setCoworkerValue)}
              invalid={validation.invalid}
              invalidText={validation.invalidText ? validation.invalidText : ""}
            />
          </div>
        )
      )}
      {step === STEPS.CHAT && (
        <div className="client_binding_main">
          <TextInput
            id="client-input"
            labelText={t("main.chat.addModal.title", { context: "chat" })}
            helperText={`${t("main.chat.clientBindingModal.helperChatText")}`}
            placeholder={`${t("main.chat.addModal.placeholder", {
              context: "chat",
            })}`}
            value={inputValue}
            onChange={handleInputChange(setInputValue)}
            required
            invalid={validation.invalid}
            invalidText={validation.invalidText ? validation.invalidText : ""}
          />
        </div>
      )}
      {step === STEPS.MEMBERS && (
        <div className="client_binding_main">
          <CheckboxGroup
            className="client_binding_main_CheckboxGroup"
            helperText={`${t(
              "main.chat.clientBindingModal.helperMembersText",
            )}`}
            legendText={`${t(
              "main.chat.clientBindingModal.legendMembersText",
            )}`}
          >
            {allMembers.map((member) => (
              <Checkbox
                key={member.chataccid}
                id={`member-${member.chataccid}`}
                labelText={member.name || `Unnamed (${member.chataccid})`}
                checked={member.checked}
                onChange={() => handleCheckboxChange(member.chataccid)}
              />
            ))}
          </CheckboxGroup>
        </div>
      )}
      {notification.visibility && (
        <InlineNotification
          title={t(notification.title)}
          subtitle={t(notification.subtitle)}
          kind={notification.kind}
          onClose={() =>
            setNotification({
              visibility: false,
              title: "",
              subtitle: "",
              kind: "",
            })
          }
          lowContrast={true}
          aria-label={t("authentication.notification.closeNotification")}
          className="notification"
        />
      )}
    </Modal>
  );
}
